import type { paths } from "../deferred-api/contracts";
import type { InvitationRecord } from "./models";

type InvitationListRoute = paths["/v1/admin/user-invitations"]["get"];
export type InvitationFilters = NonNullable<InvitationListRoute["parameters"]["query"]>;
export type InvitationPage = Omit<
  InvitationListRoute["responses"][200]["content"]["application/json"],
  "items"
> & { items: InvitationRecord[] };
export const defaultInvitationFilters = { page: 1, pageSize: 20 } satisfies InvitationFilters;

export type UserSecurityInput = NonNullable<
  paths["/v1/admin/users/{userId}/suspend"]["post"]["requestBody"]
>["content"]["application/json"];
export type UserRolesInput = NonNullable<
  paths["/v1/admin/users/{userId}/roles"]["put"]["requestBody"]
>["content"]["application/json"];
export type InvitationCreateInput = NonNullable<
  paths["/v1/admin/user-invitations"]["post"]["requestBody"]
>["content"]["application/json"];
export type InvitationChangeInput = NonNullable<
  paths["/v1/admin/user-invitations/{invitationId}/resend"]["post"]["requestBody"]
>["content"]["application/json"];
export type UserMutationReceipt =
  | paths["/v1/admin/administrators/{userId}/restore"]["post"]["responses"][200]["content"]["application/json"]
  | paths["/v1/admin/users/{userId}/suspend"]["post"]["responses"][200]["content"]["application/json"]
  | paths["/v1/admin/users/{userId}/sessions/revoke"]["post"]["responses"][200]["content"]["application/json"]
  | paths["/v1/admin/users/{userId}/roles"]["put"]["responses"][200]["content"]["application/json"]
  | Omit<
      paths["/v1/admin/administrators/{userId}/reset-mfa"]["post"]["responses"][200]["content"]["application/json"],
      "mfaEnrolledAt"
    >;

export type IdentityInputs = {
  USER_SUSPEND: UserSecurityInput;
  USER_BAN: UserSecurityInput;
  USER_RESTORE: UserSecurityInput;
  USER_SESSIONS_REVOKE: UserSecurityInput;
  USER_ROLES_REPLACE: UserRolesInput;
  USER_MFA_RESET: UserSecurityInput;
  INVITATION_CREATE: InvitationCreateInput;
  INVITATION_RESEND: InvitationChangeInput;
  INVITATION_CANCEL: InvitationChangeInput;
};
export type IdentityOperation = keyof IdentityInputs;
export type IdentityCommand = {
  [K in IdentityOperation]: {
    operation: K;
    input: IdentityInputs[K];
    targetId: string;
    parentId: string;
  };
}[IdentityOperation];

export function governedActions(operation: string, input: object): string[] {
  if (
    (operation === "DESTINATION_LIFECYCLE" || operation === "EXPERIENCE_LIFECYCLE") &&
    "relatedActivation" in input
  ) {
    const related = input.relatedActivation as { selectedKeys?: unknown };
    const keys = related && Array.isArray(related.selectedKeys) ? related.selectedKeys : [];
    return [
      ...new Set([
        operation,
        ...(keys.some((key) => typeof key === "string" && key.startsWith("EXPERIENCE:"))
          ? ["EXPERIENCE_LIFECYCLE"]
          : []),
        ...(keys.some((key) => typeof key === "string" && key.startsWith("PARTNER:"))
          ? ["PARTNER_LIFECYCLE"]
          : []),
        ...(keys.some((key) => typeof key === "string" && key.startsWith("ACTIVITY:"))
          ? ["OFFERING_LIFECYCLE"]
          : []),
      ]),
    ];
  }

  return [
    operation,
    ...(Object.hasOwn(input, "internalNotes") ? ["OFFERING_NOTES_WRITE"] : []),
    ...(operation === "INVITATION_CREATE"
      ? [
          "INVITATION_ACTIVATE",
          ...("optionalRoles" in input &&
          Array.isArray(input.optionalRoles) &&
          input.optionalRoles.length
            ? ["INVITATION_ROLE_GRANT"]
            : "partnerAccess" in input
              ? ["INVITATION_ROLE_GRANT"]
              : []),
        ]
      : []),
  ];
}
